const DEFAULT_SITE_URL = 'https://f1-elo-rankings.pages.dev'
const CONFIGURED_SITE_URL = import.meta.env.VITE_SITE_URL
export const SITE_URL = (CONFIGURED_SITE_URL || DEFAULT_SITE_URL).replace(/\/+$/, '')

const toAbsoluteUrl = (path = '/') => {
  if (/^https?:\/\//i.test(path)) return path
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${SITE_URL}${normalizedPath}`
}

export const buildWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}#website`,
  name: 'F1 ELO Rankings',
  url: SITE_URL,
  description: 'Formula 1 driver rankings powered by an Elo rating system.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/all-time?search={search_term_string}`,
    'query-input': 'required name=search_term_string'
  }
})

export const buildDatasetSchema = ({ path = '/' } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  '@id': `${toAbsoluteUrl(path)}#dataset-${String(path).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'root'}`,
  name: 'F1 Driver Elo Ratings Dataset',
  description: 'Formula 1 driver Elo ratings and race history spanning the world championship era.',
  url: toAbsoluteUrl(path),
  temporalCoverage: '1950/2026',
  sameAs: [
    'https://github.com/Penguin0011/F1-ELO-Website',
    'https://f1-elo-api.spacey.workers.dev'
  ],
  creator: {
    '@type': 'Organization',
    '@id': `${SITE_URL}#organization`,
    name: 'F1 ELO Rankings'
  }
})

export const buildBreadcrumbSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: toAbsoluteUrl(item.path)
  }))
})

export const buildDriverSchema = ({
  path,
  name,
  image,
  description,
  wins,
  championships,
  sameAs = [],
  worksForId
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Person',
  '@id': toAbsoluteUrl(path),
  name,
  url: toAbsoluteUrl(path),
  jobTitle: 'Formula 1 Driver',
  description,
  knowsAbout: ['Formula 1', 'Motor racing'],
  ...(image ? { image: toAbsoluteUrl(image) } : {}),
  ...(sameAs.length ? { sameAs } : {}),
  ...(worksForId ? { worksFor: { '@id': worksForId } } : {}),
  ...((championships > 0 || wins > 0)
    ? {
        award: championships > 0
          ? `${championships} Formula 1 ${championships === 1 ? 'World Championship' : 'World Championships'}`
          : `${wins} Formula 1 race ${wins === 1 ? 'win' : 'wins'}`
      }
    : {})
})

export const buildSportsOrganizationSchema = ({
  name,
  slug,
  description,
  sameAs = [],
  memberIds = []
}) => ({
  '@context': 'https://schema.org',
  '@type': 'SportsOrganization',
  '@id': `${SITE_URL}#team-${slug}`,
  name,
  ...(description ? { description } : {}),
  ...(sameAs.length ? { sameAs } : {}),
  ...(memberIds.length
    ? {
        member: memberIds.map(id => ({ '@id': id }))
      }
    : {})
})

export const buildItemListSchema = ({ name, description, path, items = [] }) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name,
  description,
  url: toAbsoluteUrl(path),
  numberOfItems: items.length,
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    url: toAbsoluteUrl(item.path)
  }))
})

export const buildFaqSchema = (questions = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer
    }
  }))
})
